import { Outlet, useLocation } from 'react-router-dom';
import { Bell, ChevronRight } from 'lucide-react';
import { useAuthStore, useAppStore } from '@/store';
import Sidebar from '@/components/Sidebar';
import GlobalSearch from '@/components/GlobalSearch';
import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';


// ─── Breadcrumb map ─────────────────────────────────────────────────────────

const breadcrumbLabels: Record<string, string> = {
  '': 'Dashboard',
  entities: 'Entities',
  'chart-of-accounts': 'Chart of Accounts',
  'trial-balances': 'Trial Balances',
  'financial-reports': 'Financial Reports',
  consolidation: 'Consolidation',
  reports: 'Reports',
  settings: 'Settings',
  audit: 'Audit Trail',
  tax: 'Tax Computation',
  'transfer-pricing': 'Transfer Pricing',
  anomalies: 'Anomaly Detection',
  'nl-query': 'Natural Language Query',
  forecasting: 'Forecasting',
  scenarios: 'Scenario Modeling',
  xbrl: 'XBRL Management',
  esg: 'ESG Dashboard',
  'multi-gaap': 'Multi-GAAP Conversion',
  approvals: 'Approval Workflows',
  'blockchain-audit': 'Blockchain Audit',
  marketplace: 'Skill Marketplace',
  'data-quality': 'Data Quality',
  'predictive-close': 'Predictive Close',
  kpi: 'KPI Dashboard',
  'close-status': 'Close Status',
  rules: 'Rules Engine',
  budget: 'Budget & Forecast',
};

// ─── Layout Component ───────────────────────────────────────────────────────

export default function Layout() {
  const { user } = useAuthStore();
  const {
    sidebarCollapsed,
    notifications,
    markNotificationRead,
  } = useAppStore();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Build breadcrumbs
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    ...pathSegments.map((segment, index) => ({
      label: breadcrumbLabels[segment] || segment,
      path: '/' + pathSegments.slice(0, index + 1).join('/'),
    })),
  ];

  // Close notification dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <Sidebar />

      {/* Main content area */}
      <div
        className={clsx(
          'flex flex-col flex-1 min-w-0 transition-all duration-300',
          sidebarCollapsed ? 'ml-16' : 'ml-64',
        )}
      >
        {/* Top header */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-14 px-6 bg-white border-b border-gray-200">
          {/* Breadcrumbs */}
          <nav className="flex items-center text-sm">
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.path} className="flex items-center">
                {index > 0 && (
                  <ChevronRight className="w-3.5 h-3.5 mx-1.5 text-gray-400" />
                )}
                <span
                  className={clsx(
                    index === breadcrumbs.length - 1
                      ? 'text-gray-900 font-medium'
                      : 'text-gray-500',
                  )}
                >
                  {crumb.label}
                </span>
              </span>
            ))}
          </nav>

          {/* Right side: search, notifications, user */}
          <div className="flex items-center gap-4">
            {/* Global Search */}
            <GlobalSearch />

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Bell className="w-4.5 h-4.5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="badge bg-primary-100 text-primary-700">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto scrollbar-thin">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-gray-400">
                        No notifications
                      </div>
                    ) : (
                      notifications.slice(0, 10).map((notif) => (
                        <div
                          key={notif.id}
                          onClick={() => markNotificationRead(notif.id)}
                          className={clsx(
                            'px-4 py-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors',
                            !notif.read && 'bg-blue-50/50',
                          )}
                        >
                          <div className="flex items-start gap-2">
                            <div
                              className={clsx(
                                'w-2 h-2 mt-1.5 rounded-full flex-shrink-0',
                                notif.type === 'success' && 'bg-green-500',
                                notif.type === 'error' && 'bg-red-500',
                                notif.type === 'warning' && 'bg-amber-500',
                                notif.type === 'info' && 'bg-blue-500',
                              )}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {notif.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {notif.message}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User info */}
            <div className="flex items-center gap-2 pl-4 border-l border-gray-200">
              <div className="w-8 h-8 rounded-full bg-primary-700 flex items-center justify-center text-white text-xs font-semibold">
                {user?.full_name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) || 'U'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-700 leading-tight">
                  {user?.full_name || 'User'}
                </p>
                <p className="text-xs text-gray-400 capitalize">{user?.role || 'viewer'}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
